// src/encounter/lib/from-questionnaire-response-resource.ts
// Reverse mapper (PROD-1354c reload): the stored QB `QuestionnaireResponseResource` (wire) → the
// DS renderer's flat QuestionnaireResponse (linkId → value), so a reopened encounter rehydrates
// its prior answers. Inverse of to-questionnaire-response-resource.ts:
//  • valueCoding → the DS choice KEY (`code ?? display` — the renderer's optionValue), so the
//    selection matches back onto the same answerOption chip.
//  • valueDateTime → a Date instance (the DS DatePicker renders only `v instanceof Date`).
//  • a `repeats` item ALWAYS hydrates as string[] — the DS multi-select treats a scalar as
//    unselected, so a single saved selection must still arrive as a one-element array. Multiple
//    answers on one linkId imply multi even without meta.
import type { QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type {
  QuestionnaireResponseResource,
  QRItemWire,
  QRAnswerWire,
} from '@/types/questionnaire-response-save-api';
import type { QrItemMetaMap } from './qr-item-meta';
import { isMachineSuggested } from './to-questionnaire-response-resource';

type QRAnswerItem = QuestionnaireResponse['answers'][number];

/** One wire answer → the DS scalar it round-trips to (undefined = nothing usable). */
function answerValue(a: QRAnswerWire): string | number | boolean | Date | undefined {
  if (a.valueBoolean != null) return a.valueBoolean;
  if (a.valueInteger != null) return a.valueInteger;
  if (a.valueDecimal != null) return a.valueDecimal;
  if (a.valueDateTime != null) {
    const d = new Date(a.valueDateTime);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  if (a.valueCoding != null) return a.valueCoding.code ?? a.valueCoding.display ?? undefined;
  if (a.valueString != null) return a.valueString;
  return undefined;
}

/** As a multi-select entry the DS stores string keys — stringify non-strings stably. */
function toKey(v: string | number | boolean | Date): string {
  return v instanceof Date ? v.toISOString() : String(v);
}

/** Stored wire QR → the DS flat response (hydrates `QuestionnaireRenderer` via `response`). */
export function fromQuestionnaireResponseResource(
  resource: QuestionnaireResponseResource,
  itemMeta?: QrItemMetaMap,
): QuestionnaireResponse {
  const answers: QRAnswerItem[] = [];
  const walk = (items: QRItemWire[] | undefined | null) => {
    for (const it of items ?? []) {
      const vals = (it.answer ?? [])
        .map(answerValue)
        .filter((v): v is string | number | boolean | Date => v !== undefined && v !== '');
      if (vals.length > 0) {
        const multi = itemMeta?.get(it.linkId)?.repeats === true || vals.length > 1;
        // Restore AI provenance. Without this the DS-021 cue vanished on reload and model-drafted
        // prose presented as the clinician's own — the round trip lost the one fact that says whose
        // words these are. Only set when true, mirroring the serialiser. Accepts the canonical
        // QuestionBank extension AND the legacy flat url (PROD-2537), so older rows keep their cue.
        const byModel = isMachineSuggested(it.extension);
        answers.push({
          linkId: it.linkId,
          value: multi ? vals.map(toKey) : vals[0],
          ...(byModel ? { aiAuthored: true } : {}),
        });
      }
      walk(it.item); // groups nest via `item`
    }
  };
  walk(resource.item);
  return {
    resourceType: 'QuestionnaireResponse',
    status: resource.status === 'completed' ? 'completed' : 'in-progress',
    answers,
  };
}
